import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import { isUndefined, keyBy, xorBy } from 'lodash-es'
import {
    CountryContract,
    CustomerListContract,
    CustomerLookupContract,
    CustomerUpdateContract,
    NotificationChannelContract,
} from 'models'
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import customerService from 'Services/api/customer.service'

export enum CUSTOMER_SELL_POPUP_ROUTE {
    main = 'main',
    customer_insert = 'customer_insert',
    customer_update = 'customer_update',
    address = 'address',
    address_insert = 'address_insert',
    address_update = 'address_update',
    customer_accounts = 'customer_accounts',
}

export interface ICustomerSellPopupProviderProps {
    popupDefaultCustomerId: string
    notificationChannels: NotificationChannelContract[]
    country: CountryContract
    onClickAssignToOrder: (
        orderID: string,
        customerId: string,
        customerAddressId: string,
    ) => Promise<void> | void
    assignCustomerLoading?: boolean
}

export interface ICustomerSellPopupContext
    extends ICustomerSellPopupProviderProps {
    customerList: CustomerLookupContract[]
    curRoute: CUSTOMER_SELL_POPUP_ROUTE
    setCurRoute: (route: CUSTOMER_SELL_POPUP_ROUTE) => void
    customer?: CustomerUpdateContract
    popupSelectAddressId?: string
    setPopupSelectAddressId: (id: string) => void
    popupSelectCustomerId?: string
    onSelectCustomer: (id: string | undefined) => void
    fetchCustomers: () => void
    fetchCustomerById: (id: string) => void
    customersLoading: boolean
    updateCustomer: (
        customerId: string,
        customer: CustomerUpdateContract,
    ) => Promise<unknown>
    createCustomer: (
        customer: CustomerUpdateContract,
    ) => Promise<CustomerLookupContract | undefined>
    clearSelectCustomer: () => void
    getCustomer: (cusId: string) => CustomerLookupContract | undefined
    // active toggle customer
    onToggleCustomerActive: () => Promise<void>
    onToggleLoading: boolean
    queryText: string
    setQueryText: (text: string) => void
    selectedCustomer: CustomerLookupContract
    restoreToDefaultCustomer: () => void
    title: string
    onNewCustomer: () => void
}

const CustomerSellPopupContext = createContext<ICustomerSellPopupContext>(
    {} as ICustomerSellPopupContext,
)

export function SaleCustomerSellPopupContextProvider({
    notificationChannels,
    country,
    children,
    popupDefaultCustomerId,
    onClickAssignToOrder,
    assignCustomerLoading = false,
}: ICustomerSellPopupProviderProps & { children: ReactNode }) {
    const [curRoute, setCurRoute] = useState<CUSTOMER_SELL_POPUP_ROUTE>(
        CUSTOMER_SELL_POPUP_ROUTE.main,
    )

    const [, fetchCustomers, fetchCustomersStatus] = useFetchApi(
        customerService.getPOSCustomers,
    )

    const [
        customer,
        fetchCustomerDetails,
        fetchCustomerDetailsStatus,
        ,
        clearCustomerDetails,
        ,
    ] = useFetchApi(customerService.getCustomerById)

    const [, updateCustomer, updateCustomerStatus] = useFetchApi(
        customerService.updateCustomerById,
    )

    const [, createCustomer, createCustomerStatus] = useFetchApi(
        customerService.createCustomer,
    )
    const [, activateCustomerApi, activateCustomerStatus] = useFetchApi(
        customerService.activateCustomer,
        {
            showSuccessToast: true,
            successMessage: 'Activate customer successfully.',
            showErrorToast: true,
        },
    )
    const [, deactivateCustomerApi, deactivateCustomerStatus] = useFetchApi(
        customerService.deactivateCustomer,
        {
            showSuccessToast: true,
            successMessage: 'Deactivate customer successfully.',
            showErrorToast: true,
        },
    )
    const [popupSelectAddressId, setPopupSelectAddressId] = useState<string>('')
    const [popupSelectCustomerId, setPopupSelectCustomerId] = useState<string>()
    const [steadyCustomerLookups, setSteadyCustomerLookups] = useState<
        CustomerListContract[]
    >([])
    const [queryText, setQueryText] = useState('')

    const syncCurRoute = useCallback(
        (route: CUSTOMER_SELL_POPUP_ROUTE) => {
            setCurRoute(route)
        },
        [setCurRoute],
    )

    const onSelectCustomer = useCallback(
        (customerId: string | undefined) => {
            setPopupSelectCustomerId(customerId || popupDefaultCustomerId)
            setPopupSelectAddressId('')
        },
        [popupDefaultCustomerId],
    )
    const onFetchingCustomer = useCallback(async () => {
        const customers = await fetchCustomers()
        const customerMap = keyBy(customers ?? [], (c) => c.customerID!)
        setSteadyCustomerLookups((old) => {
            if (!customers) return old
            let newCustomers = old
                .map((c) => {
                    return customerMap[c.customerID!]
                })
                .filter((a) => !isUndefined(a))
            newCustomers = [
                ...newCustomers,
                ...xorBy(newCustomers, customers, (c) => c.customerID),
            ]
            return newCustomers
        })
    }, [fetchCustomers])
    const { t } = useTranslation()
    const title = useMemo(
        () =>
            curRoute === CUSTOMER_SELL_POPUP_ROUTE.main
                ? t('quick_sell.button.customers')
                : curRoute === CUSTOMER_SELL_POPUP_ROUTE.customer_insert
                ? t('quick_sell.customer.new_customer')
                : curRoute === CUSTOMER_SELL_POPUP_ROUTE.customer_update
                ? t('quick_sell.customer.update_customer')
                : curRoute === CUSTOMER_SELL_POPUP_ROUTE.address_insert
                ? t('quick_sell.customer.new_address')
                : curRoute === CUSTOMER_SELL_POPUP_ROUTE.address_update
                ? t('quick_sell.customer.update_address')
                : t('quick_sell.customer.manage_address'),
        [curRoute, t],
    )

    const contexts = useMemo<Omit<ICustomerSellPopupContext, 'title'>>(() => {
        const activateCustomer = async (id: string) => {
            await activateCustomerApi(id)
            setSteadyCustomerLookups((cs) =>
                cs.map((c) => {
                    if (c.customerID === id) c.isActive = true
                    return c
                }),
            )
            return true
        }
        const deactivateCustomer = async (id: string) => {
            await deactivateCustomerApi(id)
            setSteadyCustomerLookups((cs) =>
                cs.map((c) => {
                    if (c.customerID === id) c.isActive = false
                    return c
                }),
            )
            return false
        }

        const getCustomer = (customerId: string) =>
            steadyCustomerLookups?.find((f) => f.customerID === customerId)
        const selectedCustomer = steadyCustomerLookups?.find(
            (f) => f.customerID === popupSelectCustomerId,
        ) || {
            customerID: popupDefaultCustomerId,
        }
        const onToggleCustomerActive = async () => {
            if (selectedCustomer) {
                var isActive = selectedCustomer?.isActive ?? false

                if (isActive)
                    await deactivateCustomer(selectedCustomer?.customerID!)
                else await activateCustomer(selectedCustomer?.customerID!)

                var newCustomerList = steadyCustomerLookups.map((c) => {
                    if (c.customerID === selectedCustomer.customerID)
                        return { ...c, isActive: !isActive }
                    return c
                })
                setSteadyCustomerLookups(newCustomerList)
            }
        }
        const restoreToDefaultCustomer = () => {
            setPopupSelectCustomerId(
                steadyCustomerLookups.some(
                    (i) => i.customerID === popupDefaultCustomerId,
                )
                    ? popupDefaultCustomerId
                    : undefined,
            )
        }
        const onNewCustomer = () => {
            setPopupSelectCustomerId(undefined)
            setCurRoute(CUSTOMER_SELL_POPUP_ROUTE.customer_insert)
        }
        return {
            curRoute,
            setCurRoute: syncCurRoute,
            customerList: steadyCustomerLookups,
            popupSelectAddressId,
            setPopupSelectAddressId,
            fetchCustomers: onFetchingCustomer,
            popupSelectCustomerId,
            onSelectCustomer,
            fetchCustomerById: fetchCustomerDetails,
            customersLoading: isLoading([fetchCustomersStatus]),
            customerLoading: isLoading([
                fetchCustomerDetailsStatus,
                createCustomerStatus,
                updateCustomerStatus,
            ]),
            popupDefaultCustomerId,
            notificationChannels,
            country,
            createCustomer,
            updateCustomer,
            customer,
            clearSelectCustomer: clearCustomerDetails,
            onClickAssignToOrder,

            onToggleLoading: isLoading([
                activateCustomerStatus,
                deactivateCustomerStatus,
            ]),
            assignCustomerLoading,
            queryText,
            setQueryText,
            getCustomer,
            selectedCustomer,
            onToggleCustomerActive,
            restoreToDefaultCustomer,
            onNewCustomer,
        }
    }, [
        steadyCustomerLookups,
        popupDefaultCustomerId,
        curRoute,
        syncCurRoute,
        popupSelectAddressId,
        onFetchingCustomer,
        popupSelectCustomerId,
        onSelectCustomer,
        fetchCustomerDetails,
        fetchCustomersStatus,
        fetchCustomerDetailsStatus,
        createCustomerStatus,
        updateCustomerStatus,
        notificationChannels,
        country,
        createCustomer,
        updateCustomer,
        customer,
        clearCustomerDetails,
        onClickAssignToOrder,
        activateCustomerStatus,
        deactivateCustomerStatus,
        assignCustomerLoading,
        queryText,
        activateCustomerApi,
        deactivateCustomerApi,
    ])
    // fetch when initial context for mapping customer in
    // order lookup
    // table lookup
    // default order customer
    useEffect(() => {
        onFetchingCustomer()
    }, [onFetchingCustomer])

    return (
        <CustomerSellPopupContext.Provider value={{ ...contexts, title }}>
            {children}
        </CustomerSellPopupContext.Provider>
    )
}

export default CustomerSellPopupContext
export const useCustomerSellPopupContext = () =>
    useContext(CustomerSellPopupContext)
